/**
 * SessionStorage copy utility for web-helio
 * Handles copying sessionStorage data ONLY when opening new tabs
 * After copying, each tab remains independent
 */

interface SessionStorageCopyOptions {
  keysToSync?: string[];
  excludeKeys?: string[];
}

class SessionStorageCopy {
  private keysToSync: string[];
  private excludeKeys: string[];
  private isInitialized: boolean = false;

  constructor(options: SessionStorageCopyOptions = {}) {
    this.keysToSync = options.keysToSync || [];
    this.excludeKeys = options.excludeKeys || ['temp_', 'cache_'];
    this.init();
  }

  /**
   * Initialize the sessionStorage copy functionality
   */
  private init(): void {
    if (this.isInitialized) return;

    // Copy sessionStorage from parent tab when this is a new tab
    this.copySessionStorageFromParent();

    // Listen for beforeunload to prepare data for new tabs
    this.setupBeforeUnloadListener();

    // Listen for clicks that might open new tabs
    this.setupClickListeners();

    this.isInitialized = true;
  }

  /**
   * Copy sessionStorage data from the parent tab when opening a new tab
   */
  private copySessionStorageFromParent(): void {
    // Check if this is a new tab by looking for a flag
    const isNewTab = !sessionStorage.getItem('_tab_initialized');

    if (isNewTab) {
      // Get sessionStorage data from localStorage (stored by parent tab)
      const storedSessionData = localStorage.getItem('_session_storage_backup');

      if (storedSessionData) {
        try {
          const sessionData = JSON.parse(storedSessionData);

          // Copy relevant sessionStorage items ONLY ONCE
          Object.entries(sessionData).forEach(([key, value]) => {
            if (this.shouldSyncKey(key)) {
              sessionStorage.setItem(key, value as string);
            }
          });
        } catch (error) {
          console.warn('Error restoring sessionStorage from backup:', error);
        }
      }

      // Mark this tab as initialized and independent
      sessionStorage.setItem('_tab_initialized', 'true');
      sessionStorage.setItem('_tab_independent', 'true');

      // Create a unique backup key for this tab
      const tabId = this.generateTabId();
      sessionStorage.setItem('_tab_id', tabId);

      // Immediately backup this tab's state for future new tabs from this tab
      this.updateSessionStorageBackupForTab(tabId);
    }
  }

  /**
   * Generate a unique tab ID
   */
  private generateTabId(): string {
    return `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get the current tab ID, create one if it doesn't exist
   */
  private getCurrentTabId(): string {
    let tabId = sessionStorage.getItem('_tab_id');
    if (!tabId) {
      tabId = this.generateTabId();
      sessionStorage.setItem('_tab_id', tabId);
    }
    return tabId;
  }

  /**
   * Set up beforeunload listener to backup sessionStorage for potential new tabs
   */
  private setupBeforeUnloadListener(): void {
    window.addEventListener('beforeunload', () => {
      // Always backup the current tab's state before closing
      const tabId = this.getCurrentTabId();
      this.updateSessionStorageBackupForTab(tabId);
    });
  }

  /**
   * Set up click listeners to detect new tab opening
   */
  private setupClickListeners(): void {
    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      const link = target.closest('a');

      if (link && (event.ctrlKey || event.metaKey || event.button === 1)) {
        // Ctrl+click, Cmd+click, or middle mouse button
        // Backup current tab's state for the new tab
        const tabId = this.getCurrentTabId();
        this.updateSessionStorageBackupForTab(tabId);
      }
    });

    document.addEventListener('contextmenu', (event) => {
      const target = event.target as HTMLElement;
      const link = target.closest('a');

      if (link) {
        // Right-click on a link - prepare for potential new tab
        // Backup current tab's state for the new tab
        const tabId = this.getCurrentTabId();
        this.updateSessionStorageBackupForTab(tabId);
      }
    });
  }

  /**
   * Update the sessionStorage backup in localStorage for a specific tab
   */
  private updateSessionStorageBackupForTab(tabId: string): void {
    try {
      const sessionData: Record<string, string> = {};

      // Copy all sessionStorage items that should be synced
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && this.shouldSyncKey(key)) {
          sessionData[key] = sessionStorage.getItem(key) || '';
        }
      }

      // Store in localStorage with tab-specific key
      localStorage.setItem(
        `_session_storage_backup_${tabId}`,
        JSON.stringify(sessionData)
      );

      // Also update the main backup key for backward compatibility
      localStorage.setItem(
        '_session_storage_backup',
        JSON.stringify(sessionData)
      );
    } catch (error) {
      console.warn('Error backing up sessionStorage for tab:', error);
    }
  }

  /**
   * Update the sessionStorage backup in localStorage (legacy method)
   */
  private updateSessionStorageBackup(): void {
    const tabId = this.getCurrentTabId();
    this.updateSessionStorageBackupForTab(tabId);
  }

  /**
   * Check if a key should be synchronized
   */
  private shouldSyncKey(key: string): boolean {
    // If specific keys are defined, only sync those
    if (this.keysToSync.length > 0) {
      return this.keysToSync.includes(key);
    }

    // Otherwise, sync all keys except excluded ones
    return !this.excludeKeys.some((excludeKey) => key.startsWith(excludeKey));
  }

  /**
   * Manually trigger sessionStorage backup for current tab
   */
  public backupSessionStorage(): void {
    const tabId = this.getCurrentTabId();
    this.updateSessionStorageBackupForTab(tabId);
  }

  /**
   * Get current sessionStorage data that would be copied
   */
  public getCopyableData(): Record<string, string> {
    const sessionData: Record<string, string> = {};

    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && this.shouldSyncKey(key)) {
        sessionData[key] = sessionStorage.getItem(key) || '';
      }
    }

    return sessionData;
  }

  /**
   * Check if current tab is independent
   */
  public isTabIndependent(): boolean {
    return sessionStorage.getItem('_tab_independent') === 'true';
  }

  /**
   * Get current tab ID (public method)
   */
  public getTabId(): string {
    return this.getCurrentTabId();
  }

  /**
   * Clean up event listeners
   */
  public destroy(): void {
    // Note: In a real implementation, you'd want to store references to the
    // event listeners and remove them here. For simplicity, we'll leave them.
    this.isInitialized = false;
  }
}

// Create and export a default instance
const sessionStorageCopy = new SessionStorageCopy({
  // Copy the main sessionStorage keys used in the application
  keysToSync: ['user', 'agent', 'permits', 'notifications'],
});

export default sessionStorageCopy;
export { SessionStorageCopy };
